/**
 * Three-state Pass / Fail result toggle for field testing.
 *
 * Public interface: controlled `value` (`'pass' | 'fail' | null`) + `onChange`.
 * Hides the sliding indicator (neutral thumb → green/red half-pill), radiogroup
 * ARIA, and click-again-to-clear behaviour.
 *
 * Props:
 *   value     — `'pass' | 'fail' | null` (null = unselected)
 *   onChange  — (next: 'pass' | 'fail' | null) => void
 *   disabled  — disables interaction
 *   ariaLabel — accessible name for the group (default: "Test result")
 *   className — extra class(es) on the container
 *   id        — optional id prefix
 */
export default function PassFailToggle({
  value = null,
  onChange,
  disabled = false,
  ariaLabel = 'Test result',
  className = '',
  id,
  ...props
}) {
  const state = value === 'pass' || value === 'fail' ? value : 'unset';

  const select = (next) => {
    if (disabled) return;
    onChange?.(value === next ? null : next);
  };

  const handleKeyDown = (e, option) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      select('pass');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      select('fail');
    } else if (e.key === 'Escape' || e.key === 'Backspace') {
      e.preventDefault();
      if (!disabled) onChange?.(null);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      select(option);
    }
  };

  return (
    <div
      id={id}
      className={['pass-fail-toggle', className].filter(Boolean).join(' ')}
      data-state={state}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      {...props}
    >
      <span className="pass-fail-toggle__indicator" aria-hidden="true">
        {state === 'unset' && <span className="pass-fail-toggle__dash" />}
      </span>

      <button
        type="button"
        role="radio"
        aria-checked={value === 'pass'}
        disabled={disabled}
        tabIndex={value === 'fail' ? -1 : 0}
        className="pass-fail-toggle__option pass-fail-toggle__option--pass"
        onClick={() => select('pass')}
        onKeyDown={(e) => handleKeyDown(e, 'pass')}
      >
        PASS
      </button>

      <button
        type="button"
        role="radio"
        aria-checked={value === 'fail'}
        disabled={disabled}
        tabIndex={value === 'pass' ? -1 : 0}
        className="pass-fail-toggle__option pass-fail-toggle__option--fail"
        onClick={() => select('fail')}
        onKeyDown={(e) => handleKeyDown(e, 'fail')}
      >
        FAIL
      </button>
    </div>
  );
}
